import { useEffect, useState } from "react";
import { toast } from "sonner";

import LegalAdviceHeadset from "../../assets/svg/legal-advice-headset.svg";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import {
  getFollowUpDiscussionDetails,
  getFollowUpLegalAdvice,
  submitFollowUpLegalAdvice,
} from "../../api/followUpDiscussion";

type FollowUpLegalAdviceViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string, clientGoingAhead: boolean) => void;
};

export const FOLLOW_UP_LEGAL_ADVICE_FORM_ID = "follow-up-legal-advice-form";

const CLIENT_GOING_AHEAD_OPTIONS = [
  { value: "YES", label: "Yes" },
  { value: "NO", label: "No" },
] as const;

export default function FollowUpLegalAdviceView({
  leadId,
  onSuccess,
}: FollowUpLegalAdviceViewProps) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [clientGoingAhead, setClientGoingAhead] = useState<"" | "YES" | "NO">(
    ""
  );
  const [phone, setPhone] = useState<string | null>(null);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getFollowUpDiscussionDetails(leadId).catch(() => null),
      getFollowUpLegalAdvice(leadId).catch(() => null),
    ])
      .then(([details, legalAdvice]) => {
        if (cancelled) return;
        if (details?.preliminary?.phone) {
          setPhone(details.preliminary.phone);
        }
        if (legalAdvice) {
          setClientGoingAhead(legalAdvice.clientGoingAhead ? "YES" : "NO");
        }
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load legal advice"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const handleCallNow = () => {
    if (!phone) {
      toast.error("No phone number available for this lead.");
      return;
    }
    window.location.href = `tel:${phone}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    if (!clientGoingAhead) {
      toast.error("Please select whether the client is going ahead.");
      return;
    }
    const goingAhead = clientGoingAhead === "YES";
    setSaving(true);
    try {
      await submitFollowUpLegalAdvice(leadId, {
        clientGoingAhead: goingAhead,
      });
      toast.success(
        goingAhead
          ? "Legal advice recorded successfully"
          : "Process closed. Client not proceeding with follow-up."
      );
      onSuccess?.(leadId, goingAhead);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to submit legal advice"
      );
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading...</p>
      </div>
    );
  }

  return (
    <form
      id={FOLLOW_UP_LEGAL_ADVICE_FORM_ID}
      onSubmit={handleSubmit}
      className="max-w-[1085px] flex flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Phone Conversation alert box */}
      <div className="flex flex-row flex-wrap items-center justify-between gap-5 p-5 rounded-2xl border border-dashed border-[#E18308] bg-[#FFECD3]">
        <div className="w-[72px] h-[72px] flex-shrink-0 flex items-center justify-center">
          <img
            src={LegalAdviceHeadset}
            alt=""
            className="w-[72px] h-[72px] object-contain"
          />
        </div>
        <div className="flex-1 min-w-[200px] flex flex-col gap-2">
          <h3
            className="text-sm font-semibold text-[#222222]"
            style={{ lineHeight: "20px", letterSpacing: "0.14px" }}
          >
            Step 6: Legal Advice (Phone Conversation)
          </h3>
          <ul
            className="text-sm font-normal text-[#222222] list-disc list-inside space-y-1 pl-0"
            style={{ lineHeight: "20px" }}
          >
            <li>
              Nothing to be done here. It is a phone conversation with the
              client.
            </li>
            <li>
              If the client gets convinced that the mark they have given is not
              available for registration, they might share another mark for
              search.
            </li>
          </ul>
        </div>
        <button
          type="button"
          onClick={handleCallNow}
          className="flex-shrink-0 h-9 px-4 text-xs font-semibold text-[#222222] rounded-md bg-white border border-[#EEEEEE] hover:bg-[#F7F7F7] transition-colors"
          style={{ lineHeight: "20px" }}
        >
          Call Now
        </button>
      </div>

      <div className="flex flex-col gap-1 max-w-[520px]">
        <label
          className="text-[12px] font-normal leading-5 text-[#222222]"
          style={{
            fontFamily: "Inter, sans-serif",
            fontFeatureSettings: "'ss01' on, 'cv01' on",
          }}
        >
          Client going ahead
        </label>
        <Select
          value={clientGoingAhead || undefined}
          onValueChange={(v) => setClientGoingAhead(v as "YES" | "NO")}
        >
          <SelectTrigger className="!h-9 !min-h-0 text-[12px]">
            <SelectValue placeholder="Select an option" />
          </SelectTrigger>
          <SelectContent>
            {CLIENT_GOING_AHEAD_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </form>
  );
}
